import { QueryClient } from "@tanstack/react-query";

import {
  getGetAllConversationsInfiniteQueryKey,
  getGetConversationGroupsInfiniteQueryKey,
  useGetAllConversationsInfinite as useGeneratedGetAllConversationsInfinite,
  useGetConversationGroupsInfinite as useGeneratedGetConversationGroupsInfinite,
} from "@/api/generated";
import type {
  ConversationGroupModel,
  ConversationModel,
} from "@/api/generated/models";
import { useAuthStore } from "@/store";

export const GET_ALL_CONVERSATIONS_PAGE_SIZE = 20;
export const useGetAllConversations = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { isAuthInitialized } = useAuthStore();

  return useGeneratedGetAllConversationsInfinite(
    { limit: GET_ALL_CONVERSATIONS_PAGE_SIZE },
    {
      query: {
        enabled: isAuthInitialized && enabled,
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage?.pagination?.next_cursor,
        refetchOnWindowFocus: true,
        staleTime: 30000,
        select: (data) => {
          const lastPage = data.pages[data.pages.length - 1];

          return {
            ...data,
            conversations: data.pages.flatMap((page) => page.conversations),
            pagination: lastPage?.pagination,
          };
        },
      },
    },
  );
};

export const GET_ALL_CONVERSATION_GROUPS_PAGE_SIZE = 20;
/**
 * The history list under `keep-conversations-open`, where a row is a group of conversations
 * rather than a single one. Each group already carries what a row renders, so nothing here has
 * to be joined against {@link useGetAllConversations}.
 */
export const useGetAllConversationGroups = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { isAuthInitialized } = useAuthStore();

  return useGeneratedGetConversationGroupsInfinite(
    { limit: GET_ALL_CONVERSATION_GROUPS_PAGE_SIZE },
    {
      query: {
        enabled: isAuthInitialized && enabled,
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage?.pagination?.next_cursor,
        refetchOnWindowFocus: true,
        staleTime: 30000,
        select: (data) => {
          const lastPage = data.pages[data.pages.length - 1];

          return {
            ...data,
            conversationGroups: data.pages.flatMap(
              (page) => page.conversation_groups,
            ),
            pagination: lastPage?.pagination,
          };
        },
      },
    },
  );
};

/** Patch `has_unread` onto whichever group rows `matches` picks. */
const patchConversationGroups = (
  queryClient: QueryClient,
  hasUnread: boolean,
  matches: (group: ConversationGroupModel) => boolean,
) => {
  queryClient.setQueryData(
    getGetConversationGroupsInfiniteQueryKey({
      limit: GET_ALL_CONVERSATION_GROUPS_PAGE_SIZE,
    }),
    (old: any) => {
      if (!old?.pages) return undefined;

      return {
        ...old,
        pages: old.pages.map((page: any) => ({
          ...page,
          conversation_groups: page.conversation_groups.map(
            (group: ConversationGroupModel) =>
              matches(group) ? { ...group, has_unread: hasUnread } : group,
          ),
        })),
      };
    },
  );
};

/**
 * Patch unread state into both shapes of the history list, since only the flag decides which one
 * the sidebar is reading.
 *
 * Shared by {@link setConversationRead} and {@link setConversationUnread}; call those rather
 * than this, so a bare boolean never has to be read at a call site. These are named `set*`
 * because they only touch the query cache — `markConversationAsRead` is the generated Orval
 * function that performs the actual request.
 *
 * A group row is matched on the conversation it opens. A message in a visit names the Curai
 * conversation, which no group row carries, so that case is left to the backend's
 * `conversation_list_updated` side effect rather than guessed at here.
 *
 * A list that has not been fetched is left alone. `setQueryData` builds the entry when it is
 * missing and stamps it as freshly fetched, so writing an empty page set here would leave the
 * sidebar rendering nothing until that entry went stale — the history list mounts late (the
 * sidebar starts collapsed), so it would read the placeholder rather than fetch.
 */
const setConversationHasUnread = (
  conversationId: string,
  hasUnread: boolean,
  queryClient: QueryClient,
) => {
  queryClient.setQueryData(
    getGetAllConversationsInfiniteQueryKey({
      limit: GET_ALL_CONVERSATIONS_PAGE_SIZE,
    }),
    (old: any) => {
      if (!old?.pages) return undefined;

      return {
        ...old,
        pages: old.pages.map((page: any) => ({
          ...page,
          conversations: page.conversations.map(
            (conversation: ConversationModel) => {
              if (
                conversation.id === conversationId ||
                conversation.curai_conversation_id === conversationId
              ) {
                return { ...conversation, has_unread: hasUnread };
              }
              return conversation;
            },
          ),
        })),
      };
    },
  );

  patchConversationGroups(
    queryClient,
    hasUnread,
    (group) => group.latest_nori_conversation_id === conversationId,
  );
};

/**
 * Clear a group row's dot by group id, for a conversation the row does not name.
 *
 * A visit is read through its own id, which no row carries, so {@link setConversationRead} finds
 * nothing to clear — and once a group holds more than one Nori conversation, an older one will
 * not match either. The conversation knows its group, so the caller passes that instead.
 */
export const setConversationGroupRead = (
  conversationGroupId: string,
  queryClient: QueryClient,
) =>
  patchConversationGroups(
    queryClient,
    false,
    (group) => group.id === conversationGroupId,
  );

export const setConversationUnread = (
  conversationId: string,
  queryClient: QueryClient,
) => setConversationHasUnread(conversationId, true, queryClient);

export const setConversationRead = (
  conversationId: string,
  queryClient: QueryClient,
) => setConversationHasUnread(conversationId, false, queryClient);

/** Refetch the history list, whichever of the two shapes the sidebar is currently reading. */
export const invalidateConversationHistory = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: getGetAllConversationsInfiniteQueryKey(),
    exact: false,
  });
  queryClient.invalidateQueries({
    queryKey: getGetConversationGroupsInfiniteQueryKey(),
    exact: false,
  });
};
